import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { CurrencyCode } from 'gain-lib/money';
import { CalculationContextCacheService } from 'gain-web/app/clients/client-calculations/calculation-context-cache.service';
import {
  CacheObjectWithTimestamp,
  CalculationArgs,
  getFullCalculationEngineResultsWithTimestampCacheKey,
} from 'gain-web/app/clients/client-calculations/calculation-context.resolver';
import { ClientContextService } from 'gain-web/app/clients/client-shared/client-context.service';
import { updateRouteDataValues } from 'gain-web/lib/router/find-route-data';
import { getRouteParamFromRoot } from 'gain-web/lib/router/find-route-param';
import { ApiClient } from 'gain-web/shared-services/api-client.generated.service';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  startWith,
  takeUntil,
} from 'rxjs/operators';
import {
  CalculationContext,
  CalculationContextService,
} from './calculation-context.service';

@Injectable()
export class CalculationContextWrapperService implements OnDestroy {
  private _calculationContext$: BehaviorSubject<CalculationContext>;

  public get value(): CalculationContext {
    return this._calculationContext$.value;
  }

  public get valueChanges(): Observable<CalculationContext> {
    return this._calculationContext$.asObservable();
  }

  private _onDestroy$ = new Subject();
  private _client: ApiClient.ClientDto;
  private _clientContext: ApiClient.ClientContextDto;

  constructor(
    private _router: Router,
    private _apiCalculationContextService: ApiClient.CalculationContextService,
    private _calculationContextService: CalculationContextService,
    private _clientContextService: ClientContextService,
    private _cacheService: CalculationContextCacheService,
  ) {
    this._client = this._clientContextService.value.client;
    this._clientContext = this._clientContextService.value;
    this._calculationContext$ = new BehaviorSubject<CalculationContext>(
      this._calculationContextService.value,
    );

    this._calculationContextService.valueChanges
      .pipe(
        startWith(this._calculationContext$.value),
        distinctUntilChanged(isEqual),
        takeUntil(this._onDestroy$),
        catchError((_, caught) => caught),
      )
      .subscribe((calculationContext) =>
        this._calculationContext$.next(calculationContext),
      );
  }

  ngOnDestroy() {
    this._onDestroy$.next(undefined);
    this._onDestroy$.complete();
  }

  async refreshCalculationContext() {
    const oldCalculationContext = this._calculationContext$.getValue();

    const route = this._router.routerState.root.snapshot;
    const portalId = getRouteParamFromRoot(route, 'portalId');
    const calculationId = getRouteParamFromRoot(route, 'calculationId');

    const ccr = await this._apiCalculationContextService
      .getCalculationContextById(portalId, calculationId)
      .toPromise();

    const calcEngineResultsResponse = ccr.result.indexRecord
      .finishedOrInPostProcessing
      ? await this.getFullCalcEngineResultsWithTimestamp(
          {
            calculationId,
            clientPortalId: portalId,
          },
          ccr.result.indexRecord,
        )
      : null;

    const fullCalcEngineResults = calcEngineResultsResponse?.object;

    const calcContext = new ApiClient.CalculationContextDto({
      ...ccr.result,
      calcEngineInputs: oldCalculationContext.calcEngineInputs,
      calcEngineResults: fullCalcEngineResults?.employeeResults ?? null,
      employerCalcEngineResults: fullCalcEngineResults?.employerResults ?? null,
    });

    updateRouteDataValues(route, 'calculationContext', calcContext);

    this._calculationContext$.next(
      new CalculationContext(calcContext, this._apiCalculationContextService),
    );
  }

  async recomputeCalculationResults(currencyCode: CurrencyCode | string) {
    const route = this._router.routerState.root.snapshot;
    const calculationId = getRouteParamFromRoot(route, 'calculationId');
    const transactionGuid = getRouteParamFromRoot(route, 'transactionGuid');

    const recomputeArgs = new ApiClient.RecomputeCalculationResultsCommand({
      clientId: this._client.id,
      calculationId: calculationId,
      transactionGuid: transactionGuid,
      currencyCode: currencyCode,
      decimalNumberPrecision:
        this._clientContextService.value.numericPrecision ?? null,
      percentagePrecision:
        this._clientContextService.value.percentagePrecision ?? null,
    });

    return await this._apiCalculationContextService
      .recomputeCalculationResults(recomputeArgs)
      .toPromise();
  }

  private async getFullCalcEngineResultsWithTimestamp(
    args: CalculationArgs,
    indexRecord: ApiClient.CalculationIndexModel,
  ) {
    return this.getOrElse(
      getFullCalculationEngineResultsWithTimestampCacheKey(args),
      (json) => {
        const objectWithTimestamp: CacheObjectWithTimestamp<any> =
          JSON.parse(json);

        return {
          object: ApiClient.CalculationEngineResults.fromJS(
            objectWithTimestamp.object,
          ),
          timestamp: new Date(objectWithTimestamp.timestamp),
        };
      },
      async () => {
        const response = await this._apiCalculationContextService
          .getCalculationEngineResults(args.clientPortalId, args.calculationId)
          .toPromise();

        const calculationEngineResults = response.calculationEngineResults;
        const result: CacheObjectWithTimestamp<ApiClient.ICalculationEngineResults | null> =
          {
            timestamp: new Date(),
            object: calculationEngineResults,
          };

        return {
          valueToCache: {
            timestamp: result.timestamp,
            object: calculationEngineResults,
          },
          returnValue: result,
        };
      },
      (res) => {
        if (!res?.timestamp) {
          return false;
        }

        const timestamp =
          indexRecord.latestBrokerAdjustmentJob?.finishedTimestamp ??
          indexRecord.clientValidationEditTimestamp ??
          indexRecord.manualCalculationEditTimestamp ??
          indexRecord.calculationCompletedTimestamp;

        return !!(timestamp && res.timestamp < timestamp);
      },
    );
  }

  private async getOrElse<T>(
    key: string,
    onCacheHit: (cachedValue: string) => T,
    onCacheMiss: () => Promise<{ returnValue: T; valueToCache?: unknown }>,
    shouldRefetch: (result: T) => boolean = () => false,
  ) {
    const cached = await this._cacheService.get<string>(key);
    if (cached != null) {
      // eslint-disable-next-line no-console
      console.debug(`cache hit: ${key}`);
      const returnValue = onCacheHit(cached);

      if (!shouldRefetch(returnValue)) {
        return returnValue;
      }
    }

    // eslint-disable-next-line no-console
    console.debug(`cache miss: ${key}`);
    const { returnValue, valueToCache } = await onCacheMiss();
    if (valueToCache != null) {
      try {
        await this._cacheService.set(key, JSON.stringify(valueToCache));
      } catch (e) {
        console.error('Error caching value', e);
      }
    }
    return returnValue;
  }
}

